import { useEffect, useRef, useCallback } from 'react';
import introJs from 'intro.js';
import 'intro.js/introjs.css';
import '../styles/IntroTour.css';
import { getCookie, setCookie } from '../utils/cookies';
import { getNavbarTourSteps } from '../config/navbarTourConfig';
import { tourOptions } from '../config/tourConfig';
import { useAuth } from '../context/AuthContext';
import { useIsSmallScreen } from './useResponsive';

/**
 * Shared tour manager hook that handles:
 * - Auto-start tour logic with collision prevention
 * - Manual tour triggers with active tour checks
 * - Timer management and cleanup
 * - Navbar tour integration
 */
const useTourManager = ({
  cookieName, // e.g., 'dietRequestsTourSeen', 'trendsTourSeen'
  isAuthenticated,
  dataReady, // condition to check if data is ready (e.g., data.length > 0)
  hasSeenNavbarTour,
  pageSteps, // array of page-specific tour steps
  onTourStart = () => {}, // optional callback before tour starts
  onBeforeChange = null, // optional custom onBeforeChange callback
  onComplete = null, // optional custom onComplete callback
  onExit = null, // optional custom onExit callback
}) => {
  const { userInfo } = useAuth();
  const isSmallScreen = useIsSmallScreen();
  const autoStartTimerRef = useRef(null);

  const startTour = useCallback(() => {
    // Clear any pending auto-start timer
    if (autoStartTimerRef.current) {
      clearTimeout(autoStartTimerRef.current);
      autoStartTimerRef.current = null;
    }

    // Use the new API as recommended
    const tourInstance = introJs.tour();

    // Call optional pre-tour setup
    onTourStart();

    // Get navbar steps if navbar tour hasn't been seen
    const navbarSteps = !hasSeenNavbarTour() ? getNavbarTourSteps(userInfo) : [];

    // Merge navbar and page steps
    const combinedSteps = [...navbarSteps, ...pageSteps];

    const tour = tourInstance.setOptions({
      steps: combinedSteps,
      ...tourOptions
    });

    // Add step counter functionality
    const totalSteps = tour._options.steps.length;
    let stepCounter = 1;

    const updateStepCounter = () => {
      setTimeout(() => {
        // Remove existing counter
        const existingCounter = document.querySelector('.intro-step-counter');
        if (existingCounter) {
          existingCounter.remove();
        }

        // Create new counter
        const tooltip = document.querySelector('.introjs-tooltip');
        if (tooltip) {
          const counter = document.createElement('div');
          counter.className = 'intro-step-counter';
          counter.textContent = `${stepCounter} of ${totalSteps}`;
          tooltip.appendChild(counter);
        }
      }, 50);
    };

    tour.onstart(() => {
      stepCounter = 1;
      updateStepCounter();
    });

    tour.onbeforechange((targetElement) => {
      // Find the target step index by comparing elements
      const steps = tour._options.steps;
      let targetIndex = 0;

      for (let i = 0; i < steps.length; i++) {
        const stepElement = document.querySelector(steps[i].element);
        if (stepElement === targetElement) {
          targetIndex = i;
          break;
        }
      }

      stepCounter = targetIndex + 1;
      updateStepCounter();

      // Call custom onBeforeChange callback if provided
      if (onBeforeChange) {
        onBeforeChange(targetElement, targetIndex, steps);
      }
    });

    tour.oncomplete(() => {
      // Call custom onComplete callback if provided
      if (onComplete) {
        onComplete();
      }

      setCookie(cookieName, 'true');
      // If navbar steps were included, mark navbar tour as seen too
      if (!hasSeenNavbarTour()) {
        setCookie('navbarTourSeen', 'true');
      }
    });

    tour.onexit(() => {
      // Call custom onExit callback if provided
      if (onExit) {
        onExit();
      }

      setCookie(cookieName, 'true');
      // If navbar steps were included, mark navbar tour as seen too
      if (!hasSeenNavbarTour()) {
        setCookie('navbarTourSeen', 'true');
      }
    });

    tour.start();
  }, [pageSteps, hasSeenNavbarTour, userInfo, onTourStart, onBeforeChange, onComplete, onExit, cookieName]);

  // Auto-start tour on first visit
  useEffect(() => {
    const hasSeenTour = getCookie(cookieName);

    // Show tour if page tour hasn't been seen
    // Disable auto-start on small screens (< 768px)
    if (!hasSeenTour && isAuthenticated && dataReady && !isSmallScreen) {
      // Don't start auto tour if there's already a tour overlay visible
      if (document.querySelector('.introjs-overlay')) {
        return;
      }

      autoStartTimerRef.current = setTimeout(() => {
        // Double-check that no tour overlay is visible when timer fires
        if (!document.querySelector('.introjs-overlay')) {
          startTour();
          setCookie(cookieName, 'true');
          // If navbar steps were included, mark navbar tour as seen too
          if (!hasSeenNavbarTour()) {
            setCookie('navbarTourSeen', 'true');
          }
        }
        autoStartTimerRef.current = null;
      }, 1500);

      return () => {
        if (autoStartTimerRef.current) {
          clearTimeout(autoStartTimerRef.current);
          autoStartTimerRef.current = null;
        }
      };
    }
  }, [isAuthenticated, dataReady, hasSeenNavbarTour, isSmallScreen, cookieName, startTour]);

  return { startTour };
};

export default useTourManager;
